"use client";

import * as React from "react";
import { ChevronDownIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatDisplayDate } from "@/utils/dateFormat";

// `startMonth`/`endMonth` are optional and forwarded as-is to `Calendar`/`react-day-picker` -
// when omitted, the calendar falls back to its own default range (today -100 years to the end
// of the current year), i.e. every existing call site is unaffected unless it explicitly opts
// in. Pass them to widen/shift the year-dropdown range for a specific field (e.g. a due-date
// picker that needs future years, unlike the library default).
// `minDate` (optional) disables the days before it, e.g. an end date before its start date.
// `label` (optional) names the button for screen readers.
export function DatePicker({ selected, onChange, startMonth, endMonth, minDate, label }) {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="flex flex-col gap-3">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            id="date"
            aria-label={label ? `${label}: ${selected ? formatDisplayDate(selected) : "not set"}` : undefined}
            className="w-48 justify-between font-normal"
          >
            {selected ? formatDisplayDate(selected) : "Select date"}
            <ChevronDownIcon />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected} // opens on the chosen date's month (today's without one)
            captionLayout="dropdown"
            startMonth={startMonth}
            endMonth={endMonth}
            disabled={minDate ? { before: minDate } : undefined}
            onSelect={(date) => {
              onChange(date);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
